<template>
  <div class="manual-sync-container">
    <!-- 同步按钮 -->
    <button
      @click="triggerSync"
      :disabled="isLoading"
      :class="[
        'btn',
        isLoading ? 'btn-loading' : 'btn-primary',
        'flex items-center space-x-2'
      ]"
    >
      <svg 
        v-if="!isLoading" 
        class="w-4 h-4" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
      </svg>
      <svg 
        v-else 
        class="w-4 h-4 animate-spin" 
        fill="none" 
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>{{ isLoading ? '同步中...' : '手动同步' }}</span>
    </button>

    <!-- 同步状态显示 -->
    <div v-if="syncStatus" class="mt-3">
      <div 
        :class="[
          'p-3 rounded-lg text-sm',
          syncStatus.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        ]"
      >
        <div class="flex items-start space-x-2">
          <svg 
            v-if="syncStatus.success" 
            class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <svg 
            v-else 
            class="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
          <div class="flex-1">
            <p class="font-medium">
              {{ syncStatus.success ? '✅ 同步成功' : '❌ 同步失败' }}
            </p>
            <p class="mt-1">{{ syncStatus.message }}</p>
            <p v-if="syncStatus.timestamp" class="mt-1 text-xs opacity-75">
              {{ formatTime(syncStatus.timestamp) }}
            </p>
            
            <!-- 同步统计信息 -->
            <div v-if="syncStatus.success && syncStatus.stats" class="mt-2 space-y-1">
              <div v-if="syncStatus.stats.openCount !== undefined" class="text-xs">
                📊 开放PR: {{ syncStatus.stats.openCount }}
              </div>
              <div v-if="syncStatus.stats.mergedCount !== undefined" class="text-xs">
                📊 已合并PR: {{ syncStatus.stats.mergedCount }}
              </div>
              <div v-if="syncStatus.stats.pmcCount !== undefined" class="text-xs">
                👥 PMC相关PR: {{ syncStatus.stats.pmcCount }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 上次同步时间 -->
    <div v-if="lastSyncTime" class="mt-2 text-xs text-gray-500">
      上次同步: {{ formatTime(lastSyncTime) }}
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'

export default {
  name: 'ManualSyncButton',
  setup() {
    const isLoading = ref(false)
    const syncStatus = ref(null)
    const lastSyncTime = ref(null)

    // 触发手动同步
    const triggerSync = async () => {
      if (isLoading.value) return

      isLoading.value = true
      syncStatus.value = null

      try {
        console.log('🔄 开始手动同步...')
        
        const response = await fetch('/api/cron/daily-sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Triggered-By': 'manual-frontend'
          },
          body: JSON.stringify({
            sync_type: 'all',
            force_refresh: true,
            triggered_by: 'manual_frontend',
            timestamp: new Date().toISOString()
          })
        })

        const result = await response.json()

        if (response.ok && result.success) {
          syncStatus.value = {
            success: true,
            message: result.message || '数据同步完成',
            timestamp: new Date().toISOString(),
            stats: {
              openCount: result.openCount,
              mergedCount: result.mergedCount,
              pmcCount: result.pmcCount || result.count
            }
          }
          
          // 更新上次同步时间
          lastSyncTime.value = new Date().toISOString()
          localStorage.setItem('lastSyncTime', lastSyncTime.value)
          
          console.log('✅ 手动同步成功:', result)
          
          // 3秒后自动刷新页面数据
          setTimeout(() => {
            window.location.reload()
          }, 3000)
          
        } else {
          throw new Error(result.message || result.error || '同步失败')
        }

      } catch (error) {
        console.error('❌ 手动同步失败:', error)
        
        syncStatus.value = {
          success: false,
          message: error.message || '网络错误，请稍后重试',
          timestamp: new Date().toISOString()
        }
      } finally {
        isLoading.value = false
      }
    }

    // 格式化时间显示
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 1) {
        return '刚刚'
      } else if (diffMins < 60) {
        return `${diffMins}分钟前`
      } else if (diffHours < 24) {
        return `${diffHours}小时前`
      } else if (diffDays < 7) {
        return `${diffDays}天前`
      } else {
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }

    // 组件挂载时获取上次同步时间
    onMounted(() => {
      const stored = localStorage.getItem('lastSyncTime')
      if (stored) {
        lastSyncTime.value = stored
      }
    })

    return {
      isLoading,
      syncStatus,
      lastSyncTime,
      triggerSync,
      formatTime
    }
  }
}
</script>

<style scoped>
.btn {
  @apply px-4 py-2 rounded-lg font-medium transition-all duration-200;
}

.btn-primary {
  @apply bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2;
}

.btn-loading {
  @apply bg-gray-400 text-white cursor-not-allowed;
}

.manual-sync-container {
  @apply max-w-md;
}
</style>
